import React, { useEffect, useRef } from 'react';
import { useLeafletContext } from '@react-leaflet/core';
import L from 'leaflet';

interface AuthTileLayerProps {
  /** XYZ tile URL template, e.g. `https://host/.../{z}/{x}/{y}.png?...`. */
  url: string;
  /**
   * Complete `Authorization` header value, e.g. `Basic base64(user:token)`.
   * Pre-built here rather than per-tile so the hot path doesn't re-encode
   * on every tile fetch.
   */
  authHeader: string;
  opacity?: number;
  maxZoom?: number;
  /**
   * Top of the source tile pyramid. When set, Leaflet stops fetching
   * tiles at this zoom and upscales them for any deeper user zoom —
   * needed for sources like Flux that 404 past their native pyramid
   * depth (without it, the user can't zoom past `maxZoom` cleanly).
   */
  maxNativeZoom?: number;
  /**
   * Latitude/longitude bbox that limits which tile coords get fetched.
   * Leaflet's GridLayer skips tiles that don't intersect this bounds, so
   * passing the supply shed's bbox here cuts cold-load tile fetches by
   * ~75% for typical drive-time sheds (the rest of the viewport is
   * masked by the pane's clip-path anyway, so those fetches were
   * decoded straight into invisible pixels). Stable reference required —
   * a new instance per render would force a layer rebuild.
   */
  bounds?: L.LatLngBoundsExpression;
  /**
   * Fires on Leaflet's internal `load` event — i.e. once every tile in the
   * current viewport has finished fetching (or erroring). Used by the AGB
   * crossfade wrapper to know when it's safe to remove the outgoing year.
   * Re-fires on every pan/zoom that brings new tiles into view; callers
   * should be idempotent.
   */
  onReady?: () => void;
  /**
   * Fires `true` when Leaflet's stock `loading` event indicates new tiles
   * have started fetching (e.g. after `setUrl` swaps the template), and
   * `false` when the matching `load` event indicates the visible viewport
   * is fully painted. Optional — soil_carbon doesn't need it; the AGB
   * slider does, to drive its loading indicator.
   */
  onLoadingChange?: (loading: boolean) => void;
  /**
   * Fires once per layer instance the first time the tile server returns
   * 401 (Unauthorized) or 403 (Forbidden). Lets the parent surface an
   * explicit auth-failure banner instead of letting Leaflet paint a wall
   * of broken-tile icons. After the first auth error, the layer
   * short-circuits any further tile fetches to blank — the parent is
   * expected to either rotate the token (re-renders with a new
   * `authHeader` rebuild the layer cleanly) or hide the layer.
   */
  onAuthError?: (status: number) => void;
}

// L.TileLayer that fetches each tile via fetch() with an Authorization
// header, then feeds the resulting blob to the <img> as an object URL.
// Needed because Leaflet's default tile loader is a plain <img src=...>,
// and browsers won't attach custom headers (or credentials embedded in
// the URL) to image subresource loads.
//
// Every layer instance owns an AbortController so in-flight fetches for
// tiles the user has already scrolled past (or a year they've since moved
// off of) get cancelled on unmount rather than running to completion and
// wasting bandwidth.
//
// On the first 401/403, the layer flips a sticky `_authErrorReported`
// flag, aborts in-flight fetches, fires the parent's `onAuthError`
// callback once, and short-circuits subsequent createTile calls to a
// blank img. Without this, a stale token would fire one 401 per visible
// tile per pan/zoom — quickly into the hundreds — and Leaflet would
// paint broken-tile icons across the viewport with no signal as to why.
type AuthTileLayerInstance = L.TileLayer & {
  getTileUrl: (c: L.Coords) => string;
  options: L.TileLayerOptions & {
    authHeader: string;
    onAuthError?: (status: number) => void;
  };
  _authController: AbortController;
  _authErrorReported: boolean;
  _url: string;
};

const AuthenticatedTileLayer = L.TileLayer.extend({
  initialize(
    this: AuthTileLayerInstance,
    url: string,
    options: L.TileLayerOptions & {
      authHeader: string;
      onAuthError?: (status: number) => void;
    },
  ) {
    (L.TileLayer.prototype as unknown as { initialize: (u: string, o: L.TileLayerOptions) => void })
      .initialize.call(this, url, options);
    this.options.authHeader = options.authHeader;
    this.options.onAuthError = options.onAuthError;
    this._authController = new AbortController();
    this._authErrorReported = false;
  },
  onRemove(this: AuthTileLayerInstance, map: L.Map) {
    // Cancel any in-flight tile fetches so they don't decode into now-
    // detached <img> elements (and so Flux doesn't get paid for work we'll
    // never use).
    this._authController.abort();
    return (L.TileLayer.prototype as unknown as { onRemove: (m: L.Map) => L.Layer })
      .onRemove.call(this, map);
  },
  // Override setUrl so a year swap reuses the same Leaflet layer instance
  // (no React/DOM remount) but still aborts in-flight fetches for the old
  // year before kicking off the new ones. Base setUrl re-runs createTile
  // against existing tile coords, picking up `_url` for the new template.
  // It also skips redraw when the URL is unchanged, so a no-op call from
  // the URL-sync effect on first render is cheap.
  setUrl(this: AuthTileLayerInstance, url: string, noRedraw?: boolean): L.TileLayer {
    if (this._url !== url) {
      this._authController.abort();
      this._authController = new AbortController();
    }
    return (L.TileLayer.prototype as unknown as {
      setUrl: (u: string, n?: boolean) => L.TileLayer;
    }).setUrl.call(this, url, noRedraw);
  },
  createTile(
    this: AuthTileLayerInstance,
    coords: L.Coords,
    done: L.DoneCallback,
  ): HTMLElement {
    const img = document.createElement('img');
    img.alt = '';

    // Sticky auth-error short-circuit. If a previous tile in this layer
    // instance got 401/403, every other tile from the same auth context
    // will too — issuing the request only burns budget on a doomed call
    // and re-paints the broken-tile icon. The parent's onAuthError handler
    // is responsible for rotating the token (which rebuilds the layer
    // because authHeader is in the lifecycle effect's deps) or hiding the
    // layer.
    if (this._authErrorReported) {
      done(undefined, img);
      return img;
    }

    const tileUrl = this.getTileUrl(coords);
    const { authHeader, onAuthError } = this.options;
    const signal = this._authController.signal;

    // Earthmover Flux renders tiles dynamically from Arraylake, so cold-
    // start scaling events occasionally surface as transient 502/503/504
    // gateway errors that resolve within a second or two. Without retry
    // a viewport-load that catches Flux mid-scale paints broken tiles
    // until the user pans, which on a demo stage is a real defect.
    // Three attempts total (initial + 2 retries) at 250 / 750 ms with
    // jitter — total worst-case ~1.2 s before giving up. 4xx is not
    // retried (auth / not-found are deterministic), and AbortError
    // bails immediately.
    const MAX_RETRIES = 2;
    const retryableStatus = (s: number) => s >= 500 && s < 600;
    const sleep = (ms: number, sig: AbortSignal) =>
      new Promise<void>((resolve, reject) => {
        if (sig.aborted) {
          reject(new DOMException('aborted', 'AbortError'));
          return;
        }
        const t = window.setTimeout(resolve, ms);
        sig.addEventListener(
          'abort',
          () => {
            window.clearTimeout(t);
            reject(new DOMException('aborted', 'AbortError'));
          },
          { once: true },
        );
      });

    const fetchWithRetry = async (): Promise<Response> => {
      let lastStatus = 0;
      for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
        const r = await fetch(tileUrl, {
          headers: { Authorization: authHeader },
          signal,
        });
        if (!retryableStatus(r.status) || attempt === MAX_RETRIES) {
          return r;
        }
        lastStatus = r.status;
        // Exponential backoff with light jitter: 250 ms then 750 ms.
        const base = 250 * Math.pow(3, attempt);
        const jitter = Math.random() * 100;
        await sleep(base + jitter, signal);
      }
      // Unreachable — the loop always returns or throws — but TS needs
      // a final value when the loop variable could in theory exit.
      throw new Error(`HTTP ${lastStatus}`);
    };

    fetchWithRetry()
      .then(r => {
        if (r.status === 401 || r.status === 403) {
          // First-strike handling: latch the flag, abort other in-flight
          // tile fetches (so they don't all land as 401s in quick
          // succession), and notify the parent. Subsequent tiles will
          // hit the short-circuit at the top of createTile.
          if (!this._authErrorReported) {
            this._authErrorReported = true;
            this._authController.abort();
            onAuthError?.(r.status);
          }
          // Throw a sentinel error so the catch can recognise it and
          // suppress the broken-tile paint — the parent is expected to
          // surface the auth failure via a banner, not via tile chrome.
          throw new AuthError(r.status);
        }
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.blob();
      })
      .then(blob => {
        // Layer may have been removed (or year scrubbed past) while the
        // response was in flight; skip display so we don't decode into a
        // detached <img>.
        if (signal.aborted) {
          done(undefined, img);
          return;
        }
        const objectUrl = URL.createObjectURL(blob);
        // Handlers must be assigned before `src` — otherwise a synchronously
        // completing decode could fire `load` before we wire it up and leave
        // Leaflet's `done` hanging.
        img.onload = () => {
          URL.revokeObjectURL(objectUrl);
          done(undefined, img);
        };
        img.onerror = () => {
          URL.revokeObjectURL(objectUrl);
          done(new Error('tile decode failed'), img);
        };
        img.src = objectUrl;
      })
      .catch((err: unknown) => {
        // Aborts are expected when the user scrubs past a year mid-load.
        // Silencing them keeps Leaflet from painting a broken-tile icon.
        if (err instanceof DOMException && err.name === 'AbortError') {
          done(undefined, img);
          return;
        }
        // Auth errors handled separately — surfaced to the parent and
        // suppressed at the tile level so the viewport stays clean.
        if (err instanceof AuthError) {
          done(undefined, img);
          return;
        }
        done(err instanceof Error ? err : new Error(String(err)), img);
      });

    return img;
  },
});

// Sentinel error class for 401/403 responses so the catch arm can
// distinguish auth failures from other transport errors without parsing
// error messages.
class AuthError extends Error {
  status: number;
  constructor(status: number) {
    super(`Auth ${status}`);
    this.name = 'AuthError';
    this.status = status;
  }
}

/**
 * React wrapper for `AuthenticatedTileLayer`. Reads the current pane from
 * `useLeafletContext` so it can live inside a `<SupplyShedClipPane>` the same
 * way a stock `<TileLayer>` does.
 *
 * Two effects: a lifecycle effect that constructs the underlying Leaflet
 * layer and tears it down on unmount, and a URL-sync effect that calls
 * `setUrl` on the existing instance when the `url` prop changes. The latter
 * lets the AGB year slider swap years without rebuilding the layer, its DOM
 * container, or its event listeners — which costs a few ms per scrub but,
 * more importantly, is what kicked off the redundant pane teardown that
 * `AgbCrossfadeOverlay` was working around.
 */
export const AuthTileLayer: React.FC<AuthTileLayerProps> = ({
  url,
  authHeader,
  opacity,
  maxZoom,
  maxNativeZoom,
  bounds,
  onReady,
  onLoadingChange,
  onAuthError,
}) => {
  const context = useLeafletContext();
  const layerRef = useRef<L.TileLayer | null>(null);
  // Keep callbacks behind refs so new-closure-per-render doesn't churn the
  // lifecycle effect's deps.
  const onReadyRef = useRef(onReady);
  useEffect(() => {
    onReadyRef.current = onReady;
  }, [onReady]);
  const onLoadingChangeRef = useRef(onLoadingChange);
  useEffect(() => {
    onLoadingChangeRef.current = onLoadingChange;
  }, [onLoadingChange]);
  const onAuthErrorRef = useRef(onAuthError);
  useEffect(() => {
    onAuthErrorRef.current = onAuthError;
  }, [onAuthError]);

  // The lifecycle effect closes over the initial `url` and `opacity`. Later
  // changes propagate via the URL-sync and opacity-sync effects below — keeping
  // url/opacity OUT of the lifecycle deps is what lets a year scrub or
  // backdrop opacity flip in AgbCrossfadeOverlay reuse the same Leaflet
  // instance instead of remounting it.
  useEffect(() => {
    const map = context.map;
    const pane = context.pane;
    const layer = new (AuthenticatedTileLayer as unknown as new (
      u: string,
      o: L.TileLayerOptions & {
        authHeader: string;
        onAuthError?: (status: number) => void;
      },
    ) => L.TileLayer)(url, {
      authHeader,
      opacity,
      maxZoom,
      maxNativeZoom,
      bounds,
      pane,
      // Forward through a wrapper that reads the current callback from a
      // ref — same pattern as onReady / onLoadingChange — so the parent
      // can swap handler identity without forcing a full layer rebuild.
      onAuthError: (status: number) => onAuthErrorRef.current?.(status),
    });
    // Stock GridLayer events: `loading` fires when at least one tile has
    // started fetching after addTo / setUrl / pan; `load` fires when the
    // visible viewport is fully painted.
    const loadingHandler = () => onLoadingChangeRef.current?.(true);
    const loadHandler = () => {
      onLoadingChangeRef.current?.(false);
      onReadyRef.current?.();
    };
    layer.on('loading', loadingHandler);
    layer.on('load', loadHandler);
    layer.addTo(map);
    layerRef.current = layer;
    return () => {
      if (layerRef.current) {
        layerRef.current.off('loading', loadingHandler);
        layerRef.current.off('load', loadHandler);
        map.removeLayer(layerRef.current);
        layerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context.map, context.pane, authHeader, maxZoom, maxNativeZoom, bounds]);

  // URL-sync — the subclass's setUrl override aborts in-flight fetches for
  // the old URL; the base implementation no-ops when the URL is unchanged.
  useEffect(() => {
    layerRef.current?.setUrl(url);
  }, [url]);

  // Opacity-sync — AgbCrossfadeOverlay flips this between 0 (backdrop hidden
  // in steady state) and 0.9 (backdrop visible during a year swap).
  useEffect(() => {
    if (opacity !== undefined) {
      (layerRef.current as L.TileLayer & { setOpacity: (o: number) => void } | null)
        ?.setOpacity(opacity);
    }
  }, [opacity]);

  return null;
};
